import {
  MagicRulesFileId,
  OrgGuideFileId,
  OrgGuideFileVersionId,
  OrgId,
  UserId,
  guardNever,
} from "@hex/common";

import {
  ContextWorkbenchEntity,
  ContextWorkbenchEntityRef,
} from "../contextWorkbenchEntity";
import {
  ContextWorkbenchFile,
  ContextWorkbenchFileId,
} from "../contextWorkbenchFile";

export function getFileId(params: {
  type: ContextWorkbenchFile.Type.Entity;
  entityRef: ContextWorkbenchEntityRef;
}): ContextWorkbenchFileId {
  if (params.type === ContextWorkbenchFile.Type.Entity) {
    return getFileIdForEntityRef(params.entityRef);
  } else {
    guardNever(params.type, params.type);
    return ContextWorkbenchFileId.check("unknown");
  }
}

/**
 * Creates a deterministic file ID from an entity type and entity ID.
 * This ensures stable file IDs across sessions for the same entity.
 */
export function getFileIdForEntityRef(
  entityRef: ContextWorkbenchEntityRef,
): ContextWorkbenchFileId {
  if (entityRef.type === ContextWorkbenchEntity.Type.MagicRulesFile) {
    return getFileIdForMagicRulesFileEntity(entityRef);
  } else if (entityRef.type === ContextWorkbenchEntity.Type.OrgGuideFile) {
    return getFileIdForOrgGuideFileEntity(entityRef);
  } else {
    guardNever(entityRef, entityRef);
    return ContextWorkbenchFileId.check("unknown");
  }
}

const getFileIdForMagicRulesFileEntity = (
  entityRef: ContextWorkbenchEntityRef.MagicRulesFile,
): ContextWorkbenchFileId => {
  return ContextWorkbenchFileId.check(
    `${ContextWorkbenchFile.Type.Entity}:${entityRef.type}:${entityRef.orgId}:${entityRef.userId}:${entityRef.magicRulesFileId}`,
  );
};

const getFileIdForOrgGuideFileEntity = (
  entityRef: ContextWorkbenchEntityRef.OrgGuideFile,
): ContextWorkbenchFileId => {
  return ContextWorkbenchFileId.check(
    `${ContextWorkbenchFile.Type.Entity}:${entityRef.type}:${entityRef.orgId}:${entityRef.orgGuideFileId}:${entityRef.orgGuideFileVersionId}`,
  );
};

/**
 * Parses a composite file ID back into its entity type and entity ID components.
 * Returns null if the file ID is not in the expected format.
 */
export function parseFileId(
  fileId: ContextWorkbenchFileId,
): ContextWorkbenchEntityRef | null {
  const parts = fileId.split(":");
  if (parts.length === 0) return null;
  // Being liberal with casting here because it's parsing, kind of a necessary evil
  const fileType = parts[0] as ContextWorkbenchFile.Type;
  if (fileType === ContextWorkbenchFile.Type.Entity) {
    return parseEntityFileId(parts);
  } else {
    guardNever(fileType, fileType);
    return null;
  }
}

const parseEntityFileId = (
  parts: string[],
): ContextWorkbenchEntityRef | null => {
  const entityType = parts[1] as ContextWorkbenchEntity.Type;
  if (entityType === ContextWorkbenchEntity.Type.MagicRulesFile) {
    return parseMagicRulesFileEntityFileId(parts);
  } else if (entityType === ContextWorkbenchEntity.Type.OrgGuideFile) {
    return parseOrgGuideFileDraftEntityFileId(parts);
  } else {
    guardNever(entityType, entityType);
    return null;
  }
};

const parseMagicRulesFileEntityFileId = (
  parts: string[],
): ContextWorkbenchEntityRef.MagicRulesFile | null => {
  if (parts.length !== 5) return null;
  const orgId = OrgId.check(parts[2]);
  const userId = parts[3] === "null" ? null : UserId.check(parts[3]);
  const magicRulesFileId = MagicRulesFileId.check(parts[4]);
  return {
    type: ContextWorkbenchEntity.Type.MagicRulesFile,
    orgId,
    userId,
    magicRulesFileId,
  };
};

const parseOrgGuideFileDraftEntityFileId = (
  parts: string[],
): ContextWorkbenchEntityRef.OrgGuideFile | null => {
  if (parts.length !== 5) return null;
  const orgId = OrgId.check(parts[2]);
  const orgGuideFileId = OrgGuideFileId.check(parts[3]);
  const orgGuideFileVersionId = OrgGuideFileVersionId.check(parts[4]);
  return {
    type: ContextWorkbenchEntity.Type.OrgGuideFile,
    orgId,
    orgGuideFileId,
    orgGuideFileVersionId,
  };
};
